/**
 * Monitoramento de Dispositivos — helpers compartilhados.
 *
 * - apiErrorMessage: mensagem REAL da API (string, lista de erros do
 *   motor ou validação Pydantic) — nunca um "erro genérico" silencioso.
 * - useHierarchy: Cliente → Unidades do cliente → Áreas da unidade →
 *   Subáreas da área. Cada nível só carrega após o nível superior.
 * - funções puras de seleção de dispositivos (testadas em __tests__).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useClient } from "@/context/ClientContext";

export function apiErrorMessage(err, fallback = "Falha na operação.") {
  const detail = err?.response?.data?.detail;
  if (!detail) {
    if (err?.response?.status) return `${fallback} (HTTP ${err.response.status})`;
    return err?.message ? `${fallback} ${err.message}` : fallback;
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (typeof d === "string" ? d : d?.msg || JSON.stringify(d)))
      .join("; ");
  }
  if (detail.message) return detail.message;
  if (Array.isArray(detail.errors)) return detail.errors.join("; ");
  return JSON.stringify(detail);
}

const asList = (data) =>
  (Array.isArray(data) ? data : data?.items || []).filter(
    (x) => x && String(x.id ?? "").trim() !== ""
  );

async function fetchList(path, params, label) {
  try {
    const { data } = await api.get(path, { params });
    return asList(data);
  } catch (err) {
    toast.error(apiErrorMessage(err, `Não foi possível carregar ${label}.`));
    return [];
  }
}

/** Hierarquia em cascata — nunca carrega nível inferior sem o superior. */
export function useHierarchy({ clientId, unitId, areaId } = {}) {
  const [clients, setClients] = useState([]);
  const [units, setUnits] = useState([]);
  const [areas, setAreas] = useState([]);
  const [subareas, setSubareas] = useState([]);

  useEffect(() => {
    fetchList("/clients", undefined, "clientes").then(setClients);
  }, []);

  useEffect(() => {
    setUnits([]);
    if (!clientId) return;
    fetchList("/units", { client_id: clientId }, "unidades").then((list) =>
      setUnits(list.filter((u) => u.client_id === clientId))
    );
  }, [clientId]);

  useEffect(() => {
    setAreas([]);
    if (!unitId) return;
    fetchList("/areas", { unit_id: unitId }, "áreas").then((list) =>
      setAreas(list.filter((a) => a.unit_id === unitId))
    );
  }, [unitId]);

  useEffect(() => {
    setSubareas([]);
    if (!areaId) return;
    fetchList("/subareas", { area_id: areaId }, "subáreas").then((list) =>
      setSubareas(list.filter((s) => s.area_id === areaId))
    );
  }, [areaId]);

  return { clients, units, areas, subareas };
}

/**
 * Contexto de cliente do header: um cliente específico mantém o escopo
 * daquele cliente; "Todos"/múltiplos => "" (filtro livre + coluna Cliente).
 */
export function useContextClientId() {
  const ctx = useClient() || {};
  const ids = ctx.selectedClientIds || [];
  return ids.length === 1 ? ids[0] : "";
}

export const TYPE_LABELS = {
  porta_isca: "Porta-isca",
  trex: "T-Rex",
  armadilha_mecanica: "Armadilha mecânica",
  armadilha_luminosa: "Armadilha luminosa",
  armadilha_biologica: "Armadilha biológica",
  placa_cola: "Placa de cola",
  catrex: "Catrex",
  outro: "Outro",
};

export const typeLabel = (key) => TYPE_LABELS[key] || key || "—";

export const SCHEDULE_STATUS_STYLE = {
  RASCUNHO: "bg-slate-500/15 text-slate-500 border-slate-500/30",
  PROGRAMADO: "bg-blue-500/15 text-blue-500 border-blue-500/30",
  EM_EXECUCAO: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  PARCIAL: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  CONCLUIDO: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  CANCELADO: "bg-red-500/15 text-red-500 border-red-500/30",
};

export const inputCls =
  "w-full px-3 py-2 rounded-md border border-border bg-background text-sm disabled:opacity-50";

/* ---------------- seleção de dispositivos (funções puras) ------------- */

/** Contagem por chave (type_key | area_id) — base de "Selecionar por …". */
export function countBy(devices, key) {
  const out = {};
  for (const d of devices || []) {
    const k = d?.[key] || "—";
    out[k] = (out[k] || 0) + 1;
  }
  return out;
}

/** Marca/desmarca um conjunto de ids preservando os demais. */
export function toggleIds(selected, ids, on) {
  const next = new Set(selected);
  for (const id of ids) {
    if (on) next.add(id);
    else next.delete(id);
  }
  return next;
}

/** Ids dos dispositivos de um grupo (ex.: todos os T-Rex). */
export function idsWhere(devices, key, value) {
  return (devices || []).filter((d) => d?.[key] === value).map((d) => d.id);
}

/** Seleção só pode conter dispositivos da lista carregada (mesmo cliente/unidade). */
export function pruneSelection(selected, devices) {
  const allowed = new Set((devices || []).map((d) => d.id));
  return new Set([...selected].filter((id) => allowed.has(id)));
}
